import type {
  ManualIntervention,
  MeterUploadRow,
  MeterUploadRowData,
  TrialRunServerRow,
} from "../utils/bulkMeterUpload/buildTrialRunRow";

export type Level1WRSRef = { id: string; name: string };

export type MeterUploadDraftState = {
  meterUploadId?: string;
  level1WRS?: Level1WRSRef;
  fileName?: string;
  rows: MeterUploadRow[];
  trialServerRows: Record<string, TrialRunServerRow>;
  isValidFile: boolean;
  // Set once a ledger attempt resolves: the workflow instance it ran under, used
  // as the reference for the completion screen's audit trail (see ledgerCompleted).
  workflowInstanceId?: string;
};

export type BulkMeterUploadAction =
  | { type: "level1WRSSelected"; level1WRS: Level1WRSRef }
  | {
      type: "rowsParsedFromFile";
      rows: MeterUploadRow[];
      fileName: string;
    }
  | {
      type: "manualInterventionApplied";
      rowId: string;
      intervention: ManualIntervention;
    }
  | { type: "rowDeleted"; rowId: string }
  | { type: "rowAdded"; row: MeterUploadRow }
  // Folds a PATCHed row back in by id against live state — unlike a whole-array
  // replace, a concurrent edit resolving later can't revert this one.
  | { type: "rowReplaced"; rowId: string; row: MeterUploadRow }
  | {
      type: "ledgerCompleted";
      rows: MeterUploadRow[];
      workflowInstanceId?: string;
    }
  | {
      type: "rowEdited";
      rowId: string;
      patch: Partial<MeterUploadRowData>;
    }
  | {
      type: "trialRunCompleted";
      rowIds: string[];
      serverRows: TrialRunServerRow[];
    }
  | {
      type: "draftHydrated";
      draft: {
        meterUploadId: string;
        level1WRS: Level1WRSRef;
        fileName: string;
        rows: MeterUploadRow[];
      };
    }
  | { type: "reset" };

export const initialBulkMeterUploadState: MeterUploadDraftState = {
  rows: [],
  trialServerRows: {},
  isValidFile: false,
};

export const bulkMeterUploadReducer = (
  state: MeterUploadDraftState,
  action: BulkMeterUploadAction,
): MeterUploadDraftState => {
  switch (action.type) {
    case "level1WRSSelected":
      return { ...state, level1WRS: action.level1WRS };
    case "rowsParsedFromFile":
      return { ...state, rows: action.rows, fileName: action.fileName };
    case "rowDeleted": {
      const { [action.rowId]: _, ...rest } = state.trialServerRows;
      return {
        ...state,
        rows: state.rows.filter(r => r.id !== action.rowId),
        trialServerRows: rest,
      };
    }
    case "rowEdited":
      return {
        ...state,
        rows: state.rows.map(r =>
          r.id === action.rowId
            ? { ...r, rowData: { ...r.rowData, ...action.patch } }
            : r,
        ),
      };
    case "trialRunCompleted": {
      // Map server rows back by the rowIds that accompanied the request, not by
      // current state.rows position — between dispatch and reply a row may have
      // been added/deleted, and positional mapping would assign trial data to
      // the wrong row.
      const currentIds = new Set(state.rows.map(r => r.id));
      const next: Record<string, TrialRunServerRow> = {};
      action.rowIds.forEach((id, idx) => {
        const sr = action.serverRows[idx];
        if (sr && currentIds.has(id)) next[id] = sr;
      });
      return { ...state, trialServerRows: next };
    }
    case "draftHydrated":
      return {
        ...initialBulkMeterUploadState,
        // Keep trial results already on screen: saveDraft hydrates with id-identical
        // rows, so the value-based trial signature won't change and nothing re-runs.
        // Resetting here would wipe the grid to all-flagged until the next edit.
        // (On a fresh resume load the map is empty anyway.)
        trialServerRows: state.trialServerRows,
        ...action.draft,
        isValidFile: true,
      };
    case "reset":
      return initialBulkMeterUploadState;
    case "rowAdded":
      return { ...state, rows: [...state.rows, action.row] };
    case "rowReplaced":
      return {
        ...state,
        rows: state.rows.map(r => (r.id === action.rowId ? action.row : r)),
      };
    case "ledgerCompleted":
      return {
        ...state,
        rows: action.rows,
        workflowInstanceId: action.workflowInstanceId,
      };
    case "manualInterventionApplied":
      return {
        ...state,
        rows: state.rows.map(r =>
          r.id === action.rowId
            ? { ...r, manualIntervention: action.intervention }
            : r,
        ),
      };
    default:
      return state;
  }
};
